/* Multi-tier Free Spend for Impulse 8.0.0.
   Automatic mode: customer-selectable tiers based on merchandise value.
   Unlock & Shop: auto-earned tiers based on post-discount subtotal;
   added reward lines retain the tier active when they were added.
   Storefront-guided promotion; not secure checkout enforcement. */
(() => {
  'use strict';

  const cfg = window.FreeSpendConfig;
  if (!cfg || !cfg.enabled) return;


  // Read tier settings directly from Shopify Theme settings.
  let tiers = Array.isArray(cfg.tiers)
    ? cfg.tiers.filter(t => t && t.enabled === true)
    : [];

  tiers = tiers
    .map(t => ({
      minimum: Math.round(Number(t.minimum) * 100),
      allowance: Math.floor(Number(t.allowance))
    }))
    .filter(t =>
      Number.isFinite(t.minimum) &&
      t.minimum >= 0 &&
      Number.isInteger(t.allowance) &&
      t.allowance > 0
    )
    .sort((a, b) => a.minimum - b.minimum);

  if (!tiers.length) return;

  const mode = cfg.mode === 'unlock' ? 'unlock' : 'automatic';

  const maxCode = Math.max(
    0,
    Math.floor(Number(cfg.maxCode) || 150)
  );

  const year = String(cfg.year || '26');
  const prefix = String(cfg.prefix || 'RETAIL');
  const promotion = String(cfg.promotion || 'FS');

  const codeFor = amount =>
    `${prefix}-${amount}-${promotion}-${year}`;

  const escapeRegExp = value =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const pattern = new RegExp(
    '^' +
    escapeRegExp(prefix) +
    '-(\\d+)-' +
    escapeRegExp(promotion) +
    '-' +
    escapeRegExp(year) +
    '$',
    'i'
  );

  const isMine = code => pattern.test(String(code));

  const root =
    (window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root) || '/';

  const endpoint = path =>
    root.replace(/\/?$/, '/') + path;

  const money = cents =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cfg.currency || 'USD'
    }).format(cents / 100);

  const gross = item =>
    Number(item.original_line_price || 0);

  // IMPORTANT:
  // Must match the product eligibility of your Shopify codes.
  const eligible = item => !item.gift_card;

  const isReward = item =>
    item.properties &&
    String(
      item.properties._free_spend_reward || ''
    ).toLowerCase() === 'yes';

  const codes = cart =>
    Array.isArray(cart.discount_codes)
      ? cart.discount_codes
          .map(x => typeof x === 'string' ? x : x.code)
          .filter(Boolean)
      : null;

  const chooseTier = basis =>
    [...tiers].reverse().find(t => basis >= t.minimum);

  
  let busy = false;
  let pending = false;
  let timer;
  let lastCode = null;
  let lastSignature = null;
  let lastAvailableIndex = -1;
  let currentState = null;

  // Keep the selected tier during this browser tab's session.
  const selectionKey =
  'free-spend-selected-tier-' +
  year + '-' + mode;
  let selectedTier = 0;

  try {
    selectedTier = Math.max(
      0,
      Number(sessionStorage.getItem(selectionKey)) || 0
    );
  } catch (error) {
    // Continue if browser storage is unavailable.
  }

  function saveSelection() {
    try {
      sessionStorage.setItem(
        selectionKey,
        String(selectedTier)
      );
    } catch (error) {
      // In-page switching still works.
    }
  }

    // Calculate the reward from ONLY the customer-selected tier.
  // Values are in cents except the returned discount (whole dollars).
  function automaticReward(total, index) {
    const tier = tiers[index];

    if (!tier || total < tier.minimum) return 0;

    return Math.min(
      maxCode,
      tier.allowance,
      Math.max(
        0,
        Math.round((total - tier.minimum) / 100)
      )
    );
  }


  async function cartRequest(path, options) {
    const res = await fetch(endpoint(path), {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options
    });

    if (!res.ok) {
      throw new Error(`${path}: HTTP ${res.status}`);
    }

    return res.json();
  }

  
  function compute(cart) {
    const items = (cart.items || []).filter(eligible);

    const paid = items
      .filter(x => !isReward(x))
      .reduce((s, x) => s + gross(x), 0);

    const rewards = items
      .filter(isReward)
      .reduce((s, x) => s + gross(x), 0);

    const total = items.reduce(
      (s, x) => s + gross(x),
      0
    );

    
    
    // Automatic mode retains manual tier switching.
    // Unlock & Shop earns tiers from subtotal after the previous
    // tier's discount and reserves new allowance for later additions.
    const basis = total;
    let availableIndex = -1;
    let tier = null;
    let amount = 0;
    let nextGapCents = 0;

    if (mode === 'unlock') {
      const rewardValueByTier = Array(tiers.length).fill(0);

      items.filter(isReward).forEach(item => {
        const stage = Math.floor(
          Number(item.properties?._free_spend_tier) || 1
        );

        const index = Math.max(
          0,
          Math.min(tiers.length - 1, stage - 1)
        );

        rewardValueByTier[index] += gross(item);
      });

      // Qualify for each tier before applying that tier's
      // additional allowance to its own reward products.
      for (let i = 0; i < tiers.length; i++) {
        const qualifyingSubtotal = total - amount * 100;

        if (qualifyingSubtotal < tiers[i].minimum) {
          nextGapCents =
            tiers[i].minimum - qualifyingSubtotal;
          break;
        }

        availableIndex = i;

        const additionalReward = Math.max(
          0,
          Math.round(rewardValueByTier[i] / 100)
        );

        // Allowances are cumulative, not stacked.
        amount = Math.min(
          maxCode,
          tiers[i].allowance,
          amount + additionalReward
        );
      }

      tier = availableIndex >= 0
        ? tiers[availableIndex]
        : null;

      lastAvailableIndex = availableIndex;

    } else {
      // Preserve original Automatic Discount behavior.
      availableIndex = tiers.reduce(
        (highest, candidate, index) =>
          basis >= candidate.minimum ? index : highest,
        -1
      );

      lastAvailableIndex = availableIndex;

      const previousSelection = selectedTier;

      selectedTier = availableIndex < 0
        ? 0
        : Math.min(selectedTier, availableIndex);

      if (selectedTier !== previousSelection) {
        saveSelection();
      }

      tier = availableIndex >= 0
        ? tiers[selectedTier]
        : null;

      amount = tier
        ? automaticReward(total, selectedTier)
        : 0;

      if (tiers[availableIndex + 1]) {
        nextGapCents = Math.max(
          0,
          tiers[availableIndex + 1].minimum - basis
        );
      }
    }



    
    // Check Shopify's cart response for an applied
    // Free Spend discount, not just an unlocked tier.
    const freeSpendAllocations = (cart.items || []).some(
      item => (item.line_level_discount_allocations || [])
        .some(allocation =>
          isMine(allocation.discount_application?.title || '')
        )
    );

    const freeSpendCartDiscounts = (
      cart.cart_level_discount_applications || []
    ).some(discount => isMine(discount.title || ''));

    const freeSpendCode = (codes(cart) || []).some(isMine);

    const freeSpendApplied =
      Number(cart.total_discount || 0) > 0 &&
      (
        freeSpendAllocations ||
        freeSpendCartDiscounts ||
        freeSpendCode
      );

    return {
      paid,
      rewards,
      total,
      basis,
      tier,
      amount,
      
      
      selectedIndex: tier
        ? (mode === 'unlock' ? availableIndex : selectedTier)
        : -1,

      availableIndex,
      next: tiers[availableIndex + 1] || null,
      nextGapCents,

      cartOriginalTotal: Number(
        cart.original_total_price ??
        (cart.items || []).reduce(
          (sum, item) => sum + gross(item),
          0
        )
      ),

      cartTotal: Number(cart.total_price || 0),
      fsApplied: freeSpendApplied
    };

  }


  
  function render(state, status = '') {

    const currentIndex = state.tier
      ? tiers.indexOf(state.tier)
      : -1;

      const nextIndex = state.next
      ? tiers.indexOf(state.next)
      : -1;

    // Automatic mode uses original merchandise value.
    // Unlock & Shop uses Shopify's post-discount subtotal.
    const progressBasis = mode === 'unlock'
      ? state.cartTotal
      : state.basis;

    // Tick positions match the centers of the tier columns.
    const tickPosition = index =>
      ((index + 0.5) / tiers.length) * 100;

    // Interpolate spend between successive tier milestones.
    // Each threshold maps exactly to its corresponding tick.
    const milestones = [
      { spend: 0, position: 0 },
      ...tiers.map((tier, index) => ({
        spend: tier.minimum,
        position: tickPosition(index)
      }))
    ];

    const highestTier = tiers[tiers.length - 1];

    // Leave a short section after the final tick.
    const finalGap = tiers.length > 1
      ? Math.max(
          100,
          highestTier.minimum -
            tiers[tiers.length - 2].minimum
        )
      : Math.max(100, highestTier.minimum);

    milestones.push({
      spend: highestTier.minimum + finalGap,
      position: 100
    });

    
    let progress = 0;

    if (progressBasis >= milestones[milestones.length - 1].spend) {
      progress = 100;
    } else {
      for (let i = 1; i < milestones.length; i++) {
        const previous = milestones[i - 1];
        const current = milestones[i];

        if (progressBasis <= current.spend) {
          const fraction = Math.max(
            0,
            Math.min(
              1,
              (progressBasis - previous.spend) /
                Math.max(1, current.spend - previous.spend)
            )
          );

          progress = previous.position +
            fraction * (current.position - previous.position);

          break;
        }
      }
    }



    
    // Apply the same clean cart design to both
    // Automatic and Unlock & Shop modes whenever
    // an actual Free Spend discount is applied.
    const cleanCart = state.fsApplied;

    const showOriginalTotal = state.fsApplied;

    currentState = state;

    document.body.classList.toggle(
      'fs-clean-cart-active',
      cleanCart
    );

    document.body.classList.toggle(
      'fs-show-original-total',
      showOriginalTotal
    );

    // Update the cart drawer and full cart page.
    document.querySelectorAll('[data-subtotal]').forEach(
      subtotalElement => {
        const subtotalRow = subtotalElement.closest(
          '.cart__item-row'
        );

        if (!subtotalRow) return;

        let originalRow =
          subtotalRow.parentElement.querySelector(
            '[data-fs-original-total]'
          );

        // Create the original-price row only once.
        if (!originalRow) {
          originalRow = document.createElement('div');
          originalRow.className =
            'cart__item-sub cart__item-row';

          originalRow.dataset.fsOriginalTotal = '';

          const originalLabel =
          document.createElement('div');

          originalLabel.className = 'ajaxcart__subtotal';

          originalLabel.textContent =
          'AMOUNT BEFORE FREE SPEND';

          const originalValue =
            document.createElement('div');

          originalValue.dataset.fsOriginalValue = '';

          originalRow.append(
            originalLabel,
            originalValue
          );

          subtotalRow.before(originalRow);
        }

        // Show this row whenever Free Spend is actually applied,
        // in either Automatic or Unlock & Shop mode.
        originalRow.hidden = !showOriginalTotal;

        originalRow.querySelector(
          '[data-fs-original-value]'
        ).textContent = money(state.cartOriginalTotal);

        // Preserve the normal subtotal label.
        const subtotalLabel =
          subtotalRow.firstElementChild;

        if (subtotalLabel) {
          if (!subtotalLabel.hasAttribute(
            'data-fs-original-label'
          )) {
            subtotalLabel.dataset.fsOriginalLabel =
              subtotalLabel.textContent;
          }

          subtotalLabel.textContent = showOriginalTotal
            ? 'Subtotal'
            : subtotalLabel.dataset.fsOriginalLabel;
        }

        // Display Shopify's discounted subtotal.
        // Never change the underlying cart amount.
        if (showOriginalTotal) {
          subtotalElement.textContent =
            money(state.cartTotal);
        }
      }
    );

    document
      .querySelectorAll('[data-free-spend]')
      .forEach(node => {


        node.hidden = false;

        node.classList.toggle(
          'fs-unlocked',
          !!state.tier
        );

        const set = (selector, value) => {
          const el = node.querySelector(selector);
          if (el) el.textContent = value;
        };

        // Heading
        set('[data-fs-title]', 'FREE SPEND');

        // Current customer-selected tier
        set(
          '[data-fs-tier-summary]',
          state.tier
            ? `Tier ${currentIndex + 1}`
            : 'Unlock Tier 1'
        );

        // Update blue progress bar.
        const bar = node.querySelector('[data-fs-bar]');

        if (bar) {
          bar.style.width = `${progress}%`;
        }

        const track = node.querySelector('[data-fs-track]');

        if (track) {
          track.setAttribute(
            'aria-valuenow',
            String(Math.round(progress))
          );
        }

        
                // Display the next tier based on merchandise value only.
        let nextMessage = '';

        if (!state.tier) {
          nextMessage =
            `${money(
              Math.max(0, tiers[0].minimum - state.basis)
            )} to Tier 1`;

        } else if (
          mode === 'automatic' &&
          state.availableIndex > state.selectedIndex
        ) {
          nextMessage =
            `Tier ${state.selectedIndex + 2} upgrade available`;

        } else if (state.next) {
          nextMessage =
            `${money(state.nextGapCents)} to Tier ${nextIndex + 1}`;

        } else {
          nextMessage = 'Highest tier available';
        }

        set('[data-fs-message]', nextMessage);

        // Show only valid Upgrade/Downgrade actions.
        const actions = node.querySelector(
          '[data-fs-actions]'
        );

        if (actions) {
          actions.replaceChildren();

          const makeButton = (label, direction) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'free-spend__tier-button';
            button.dataset.fsSwitch = String(direction);
            button.textContent = label;
            return button;
          };

          if (mode === 'automatic' && state.selectedIndex > 0) {
            actions.appendChild(
              makeButton('Downgrade', -1)
            );
          }

          if (
            mode === 'automatic' &&
            state.selectedIndex >= 0 &&
            state.selectedIndex < state.availableIndex
          ) {
            actions.appendChild(
              makeButton('Upgrade', 1)
            );
          }

          actions.hidden = !actions.childElementCount;
        }



        // Render tier ticks directly on the progress bar.
        const tickContainer = node.querySelector(
          '[data-fs-ticks]'
        );

        if (tickContainer) {
          tickContainer.replaceChildren();

          tiers.forEach((tier, index) => {
            const tick = document.createElement('span');

            tick.className = 'free-spend__tick';
            tick.style.left = `${tickPosition(index)}%`;

            if (progressBasis >= tier.minimum) {
            tick.classList.add('is-earned');
            }

            tickContainer.appendChild(tick);
          });
        }

        // Render tier labels directly beneath their ticks.
        const tierContainer = node.querySelector(
          '[data-fs-tiers]'
        );

        if (tierContainer) {
          tierContainer.replaceChildren();

          tierContainer.style.setProperty(
            '--fs-tier-count',
            String(tiers.length)
          );

          tiers.forEach((tier, index) => {
            const item = document.createElement('div');
            item.className = 'free-spend__tier';

            if (progressBasis >= tier.minimum) {
            item.classList.add('is-earned');
            }

            if (index === currentIndex) {
              item.classList.add('is-current');
            }

            const name = document.createElement('span');
            name.className = 'free-spend__tier-name';
            name.textContent = `Tier ${index + 1}`;

            const details = document.createElement('span');
            details.className = 'free-spend__tier-value';
            details.textContent =
              `${money(tier.minimum)} / +${money(tier.allowance * 100)}`;

            item.append(name, details);
            tierContainer.appendChild(item);
          });
        }


        // Compact Free Spend amount.
        const statusEl = node.querySelector(
          '[data-fs-status]'
        );

        if (statusEl) {

          const rewardText = state.tier
            ? `Free Spend: ${money(state.amount * 100)} / ${money(state.tier.allowance * 100)}`
            : '';

          statusEl.textContent = status || rewardText;

          statusEl.classList.toggle(
            'free-spend__status--warning',
            Boolean(status)
          );
        }
      });

    // No reward checkbox: products added after unlocking are tagged automatically.
    document.querySelectorAll('[data-free-spend-reward-choice]')
      .forEach(node => { node.hidden = true; });
  }



  async function sync() {
    if (busy) {
      pending = true;
      return;
    }

    busy = true;

    try {
      const cart = await cartRequest('cart.js');
      const state = compute(cart);

      const desired = state.amount
        ? codeFor(state.amount)
        : '';

      const signature = JSON.stringify([
        state.total,
        state.paid,
        state.rewards,
        desired
      ]);

      const known = codes(cart);

      const other = known
        ? known.filter(x => !isMine(x))
        : [];

      if (other.length) {
        render(
          state,
          'Another discount is present; Free Spend did not replace it.'
        );
        return;
      }

      const existing = known
        ? known.find(isMine) || ''
        : null;

      const same = existing !== null
        ? existing.toUpperCase() === desired.toUpperCase()
        : (
            lastCode === desired &&
            lastSignature === signature
          );

      if (same) {
        render(state);
        return;
      }

      render(state, 'Updating discount...');

      const updated = await cartRequest(
        'cart/update.js',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            discount: desired
          })
        }
      );

      lastCode = desired;
      lastSignature = signature;

      const observed = codes(updated);

      const confirmed =
        observed !== null &&
        (
          !desired ||
          observed.some(
            x => x.toUpperCase() === desired.toUpperCase()
          )
        );

            render(
        compute(updated),
        desired && !confirmed
          ? 'Discount requested. Verify actual savings at checkout.'
          : ''
      );

      document.dispatchEvent(
        new CustomEvent('cart:build')
      );

    } catch (e) {
      console.error('[Free Spend]', e);

      document
        .querySelectorAll('[data-fs-status]')
        .forEach(x => {
          x.textContent =
            'Could not update Free Spend; verify checkout total.';
        });

    } finally {
      busy = false;

      if (pending) {
        pending = false;
        queue();
      }
    }
  }

  function queue() {
    clearTimeout(timer);
    timer = setTimeout(sync, 350);
  }

    
  function syncRewardPropertyOnForm(form) {
    if (!(form instanceof HTMLFormElement)) return;

    const rewardName = 'properties[_free_spend_reward]';
    const tierName = 'properties[_free_spend_tier]';

    let rewardInput = form.querySelector(
      `input[name="${rewardName}"]`
    );

    let tierInput = form.querySelector(
      `input[name="${tierName}"]`
    );

    const shouldMarkAsReward =
      mode === 'unlock' &&
      currentState &&
      currentState.tier;

    if (!shouldMarkAsReward) {
      if (rewardInput) rewardInput.remove();
      if (tierInput) tierInput.remove();
      return;
    }

    if (!rewardInput) {
      rewardInput = document.createElement('input');
      rewardInput.type = 'hidden';
      rewardInput.name = rewardName;
      form.appendChild(rewardInput);
    }

    rewardInput.value = 'yes';

    // Remember the tier that was active BEFORE
    // this new product was added to the cart.
    if (!tierInput) {
      tierInput = document.createElement('input');
      tierInput.type = 'hidden';
      tierInput.name = tierName;
      form.appendChild(tierInput);
    }

    tierInput.value = String(
      Math.max(1, currentState.selectedIndex + 1)
    );
  }



  // Add the Free Spend reward property automatically for new products
  // added after a tier is unlocked in Unlock & Shop mode.
  document.addEventListener(
    'submit',
    event => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('.product-single__form')) return;

      syncRewardPropertyOnForm(form);
    },
    true
  );

  
  // Respond to manual tier changes.
  // Event delegation survives cart drawer rebuilds.
  document.addEventListener('click', event => {
    const button = event.target.closest(
      '[data-fs-switch]'
    );

    if (!button) return;

    event.preventDefault();
    if (mode === 'unlock') return;

    const direction = Number(
      button.dataset.fsSwitch
    );

    if (direction !== 1 && direction !== -1) {
      return;
    }

    // Enforce the most recently computed merchandise eligibility.
    const nextTier = selectedTier + direction;

    if (
      busy ||
      nextTier < 0 ||
      nextTier > lastAvailableIndex
    ) {
      return;
    }

    selectedTier = nextTier;
    saveSelection();
    queue();
  });


  /* ==========================================
     FREE SPEND - INFORMATION POPUP
     ========================================== */

  let freeSpendModal = null;
  let freeSpendPreviousFocus = null;

  function closeFreeSpendInfo() {
    if (!freeSpendModal) return;

    freeSpendModal.remove();
    freeSpendModal = null;

    if (
      freeSpendPreviousFocus &&
      freeSpendPreviousFocus.isConnected
    ) {
      freeSpendPreviousFocus.focus();
    }

    freeSpendPreviousFocus = null;
  }

  function openFreeSpendInfo() {
    closeFreeSpendInfo();

    const isAutomatic = mode === 'automatic';

    const informationText = isAutomatic
      ? cfg.infoAutomatic
      : cfg.infoUnlock;

    freeSpendPreviousFocus = document.activeElement;

    // Full-screen background.
    const overlay = document.createElement('div');
    overlay.className = 'free-spend__modal-overlay';
    overlay.dataset.fsModalOverlay = '';

    // Modal container.
    const modal = document.createElement('div');
    modal.className = 'free-spend__modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute(
      'aria-label',
      'How Free Spend works'
    );

    // Close button.
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'free-spend__modal-close';
    closeButton.dataset.fsInfoClose = '';
    closeButton.setAttribute(
      'aria-label',
      'Close Free Spend information'
    );
    closeButton.textContent = '\u00D7';

    // Popup heading.
    
    // Render the rich-text content from Shopify theme settings.
    // Shopify's richtext setting supplies formatted HTML.
    const description = document.createElement('div');
    description.id = 'free-spend-info-description';
    description.className = 'free-spend__modal-text';

    description.innerHTML =
      typeof informationText === 'string'
        ? informationText
        : '';

    modal.append(closeButton, description);

    overlay.appendChild(modal);

    freeSpendModal = overlay;
    document.body.appendChild(overlay);

    closeButton.focus();
  }

  // Event delegation also supports rebuilt cart drawers.
  document.addEventListener('click', event => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    if (target.closest('[data-fs-info-open]')) {
      event.preventDefault();
      openFreeSpendInfo();
      return;
    }

    if (
      target.closest('[data-fs-info-close]') ||
      target === freeSpendModal
    ) {
      event.preventDefault();
      closeFreeSpendInfo();
    }
  });

  // Close on Escape and keep keyboard focus inside the popup.
  document.addEventListener('keydown', event => {
    if (!freeSpendModal) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeFreeSpendInfo();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      freeSpendModal.querySelectorAll(
        'button:not([disabled]), a[href], ' +
        'input:not([disabled]), select:not([disabled]), ' +
        'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  });


  document.addEventListener('cart:updated', queue);

    document.addEventListener('cart:build', () => {
    setTimeout(queue, 250);
  });


  document.addEventListener(
    'ajaxProduct:added',
    () => setTimeout(queue, 200)
  );

  document.addEventListener('cart:refresh', queue);
  document.addEventListener('DOMContentLoaded', queue);
  window.addEventListener('pageshow', queue);

  document.addEventListener(
    'submit',
    async event => {
      const form = event.target;

      if (
        !form.matches('#CartDrawerForm,#CartPageForm')
      ) {
        return;
      }

      const button = event.submitter;

      if (
        !button ||
        button.name !== 'checkout' ||
        form.dataset.fsResubmit === '1'
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      clearTimeout(timer);

      for (let i = 0; i < 30 && busy; i++) {
        await new Promise(
          r => setTimeout(r, 100)
        );
      }

      await sync();

      for (let i = 0; i < 30 && busy; i++) {
        await new Promise(
          r => setTimeout(r, 100)
        );
      }

      form.dataset.fsResubmit = '1';
      form.requestSubmit(button);
    },
    true
  );
})();